import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { findNextViewableAt, Order } from '../Models';

export function useKanbanRerender(orders: Order[], userId: string) {
    const [, setRerenderTrigger] = useState(0);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastScheduledTargetTime = useRef<number | null>(null);

    useEffect(
        () => () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        [],
    );

    const scheduleNextRerender = useCallback(() => {
        const nextViewableTime = findNextViewableAt(orders, userId);

        // Calculate the valueOf() for comparison, which is a number (timestamp)
        const newTargetTimeValue = nextViewableTime ? dayjs(nextViewableTime).valueOf() : null;

        // Optimization: If the new target time is the same as the last one scheduled,
        // AND there's an active timeout already set for that time, then no need to reschedule.
        if (newTargetTimeValue === lastScheduledTargetTime.current && timeoutRef.current) {
            return;
        }

        // If we reach here, we either need to clear an old timeout or set a new one.
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // If no future viewable time found, clear the target time and return.
        if (!nextViewableTime) {
            lastScheduledTargetTime.current = null;
            return;
        }

        const delay = Math.max(0, dayjs(nextViewableTime).diff(dayjs()));
        if (delay === 0) {
            return;
        }

        // Store the target time of this new schedule
        lastScheduledTargetTime.current = newTargetTimeValue;

        timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            lastScheduledTargetTime.current = null; // Clear once fired, as this event has passed.

            // Trigger a rerender by updating state
            setRerenderTrigger((prev) => prev + 1);

            // Schedule the next rerender check with the same orders
            // (orders haven't changed, but time has passed)
            scheduleNextRerender();
        }, delay);
    }, [orders, userId]);

    // Effect that runs whenever orders change
    useEffect(() => {
        scheduleNextRerender();
    }, [orders, userId, scheduleNextRerender]);
}
